import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { pluralize } from 'ember-inflector';
import set from 'ember-set-helper/helpers/set';
import DatetimePicker from 'garaje/components/datetime-picker';
import PrinterSelector from 'garaje/components/printer-selector';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import type EntryModel from 'garaje/models/entry';
import type InviteModel from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type PrinterModel from 'garaje/models/printer';
import type { ServerError } from 'garaje/services/ajax/errors';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import type { CollectionResponse, SingleResponse } from 'jsonapi/response';
import LiquidIf from 'liquid-fire/components/liquid-if';

interface InviteSignInModalSignature {
  Args: {
    invites: InviteModel[];
    printers: PrinterModel[];
    onSuccess?: () => void;
    close: () => void;
  };
}

interface EntryCheckInData {
  data: {
    attributes: {
      'entry-id': string;
      'invite-id': string;
      'location-id': string;
    };
  };
}

export default class InviteSignInModal extends Component<InviteSignInModalSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare store: StoreService;
  @service declare state: StateService;

  @tracked date = new Date();
  @tracked printBadge = true;
  @tracked notification = true;
  @tracked notificationDisabled = false;
  @tracked selectedPrinter?: PrinterModel | null = null;

  get isSaving(): boolean {
    return this.saveSignIn.isRunning;
  }

  get canSelectPrinters(): boolean {
    return this.printBadge && this.args.printers.length > 1;
  }

  get numVisitors(): number {
    const totalAdditionalGuests = this.args.invites.reduce((count, invite) => {
      return count + invite.additionalGuests;
    }, 0);
    return this.args.invites.length + totalAdditionalGuests;
  }

  saveSignIn = task(async (data: Record<string, unknown>) => {
    try {
      const result: CollectionResponse<EntryModel> = await this.ajaxFetch.request(urlBuilder.v2.entryBulkUrl(), {
        type: 'POST',
        contentType: 'application/json',
        data,
      });
      this._trackInvitesBulkSignIn(result.data.length);
      this.store.pushPayload('entry', result);
      this.flashMessages.showAndHideFlash('success', 'Signed in!');
      if (this.args.onSuccess) {
        this.args.onSuccess();
      }
      this.args.close();
      const isAllowed =
        !!this.state.features?.canAccessDesksApplication && !!this.state.currentLocationEnabledDesks?.active;
      const checkInData = this.entriesCheckInData(result.data);
      if (isAllowed && checkInData.length > 0) {
        await this.entriesCheckIn(checkInData);
      }
    } catch (e) {
      let errorText = (<ServerError<Array<{ detail: string }>>>e).payload?.at(0)?.detail;
      if (!errorText) {
        errorText = parseErrorForDisplay(e);
      }

      this.flashMessages.showAndHideFlash('error', errorText);
      throw e;
    }
  });

  async entriesCheckIn(data: EntryCheckInData[]): Promise<void> {
    await this.ajaxFetch.request(urlBuilder.rms.bulkEntryCheckIn(), {
      type: 'POST',
      contentType: 'application/vnd.api+json',
      data: data,
    });
  }

  _trackInvitesBulkSignIn(invite_count: number): void {
    this.metrics.trackEvent('Invites Bulk Sign in', { invite_count });
  }

  @action
  signIn(): void {
    const {
      selectedPrinter,
      date,
      printBadge,
      notification,
      args: { invites },
    } = this;
    const ids = invites.map((invite) => invite.id);
    const data = {
      'invite-ids': ids,
      'finalized-at': date.toISOString(),
      'print-badges': printBadge,
      'printer-id': selectedPrinter?.id,
      'notify-host': notification,
    };
    void this.saveSignIn.perform(data);
  }

  entriesCheckInData(entries: CollectionResponse<EntryModel>['data']): EntryCheckInData[] {
    const data = entries.reduce<EntryCheckInData[]>((data, entry) => {
      if (entry.attributes.employeeScreeningFlow) {
        data.push({
          data: {
            attributes: {
              'entry-id': entry.id,
              'invite-id': (<SingleResponse<InviteModel>>entry.relationships['invite']).data.id,
              'location-id': (<SingleResponse<LocationModel>>entry.relationships['location']).data.id,
            },
          },
        });
      }
      return data;
    }, []);
    return data;
  }

  <template>
    <ModalDialog @onClose={{@close}} @targetAttachment="center" @translucentOverlay={{true}}>
      <FocusTrapper>
        <div class="p-8">
          {{! Prevent focus trapper from showing the date picker automatically }}
          <div tabindex="0" class="hidden"></div>
          <h2>Sign in</h2>
          <div class="my-12 flex flex-col gap-y-4">
            <div class="flex gap-x-2">
              <DatetimePicker @value={{this.date}} @update={{set this "date"}} as |DP|>
                <span class="flex-col flex-1">
                  <label for="expectedArrivalTime">Arrival date <span class="requiredField"></span></label>
                  <DP.datePicker data-test-dp-date />
                </span>
                <span class="flex-col flex-1">
                  <label for="expectedArrivalTime">Arrival time <span class="requiredField"></span></label>
                  <DP.timePicker data-test-dp-time />
                </span>
              </DatetimePicker>
            </div>
            {{#if @printers.length}}
              <div>
                <label class="checkbox">
                  <input
                    data-test-invite-modal-print-badge
                    type="checkbox"
                    checked={{this.printBadge}}
                    {{on "click" (pick "target.checked" (set this "printBadge"))}}
                  />
                  Print badge
                </label>
                <LiquidIf @predicate={{this.canSelectPrinters}}>
                  <PrinterSelector
                    data-test-invite-modal-printer-selector
                    @placeholder="First available printer"
                    @printers={{@printers}}
                    @onChange={{fn (mut this.selectedPrinter)}}
                  />
                </LiquidIf>
              </div>
            {{/if}}
            <div>
              <label class="checkbox">
                <input
                  data-test-invite-modal-notification
                  type="checkbox"
                  checked={{this.notification}}
                  {{on "click" (pick "target.checked" (set this "notification"))}}
                />
                Send host notification
              </label>
            </div>
          </div>

          <ButtonGroup class="pt-4 flex justify-end" @reverse={{true}}>
            <UiButton
              class="btn btn--small btn--brand entries-sidebar-reprintBadge {{if this.isSaving ' isLoading'}}"
              {{on "click" this.signIn}}
              data-test-invite-modal-sign-in-button
            >
              Sign in
              {{pluralize this.numVisitors "visitor"}}
            </UiButton>
            <UiButton class="btn btn--small btn--hoverFill entries-sidebar-reprintBadge" {{on "click" @close}}>
              Cancel
            </UiButton>
          </ButtonGroup>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
